import {
  Home,
  Building2,
  Hammer,
  Map,
  TrendingUp,
  FileSearch,
  ShieldCheck,
  KeyRound,
  Scale,
  Layers,
  Repeat,
  Coins,
  Users,
  Building,
  FileStack,
  Handshake,
} from "lucide-react";
import { IMAGES } from "./assets";

export const SERVICES = {
  residential: {
    slug: "residential",
    eyebrow: "Residential",
    title: "Luxury home closings, handled with intention.",
    subtitle:
      "Whether you're buying, selling or representing the deal, we bring a white-glove standard of precision to every residential closing across Texas.",
    heroImg: IMAGES.residential,
    introImg: IMAGES.penthouse,
    introHeading: "The closing your home deserves",
    introBody:
      "Buying or selling a home is deeply personal. Houston Title Co. treats every residential closing with the discretion, communication and care of a private-client experience — keeping buyers, sellers and their agents informed and confident at every step.",
    perspectives: {
      heading: "Built around every side of the table.",
      body: "A residential closing brings three parties together — each with different priorities. We serve all of them with the same standard of clarity, communication and care.",
      items: [
        {
          icon: KeyRound,
          label: "For Buyers",
          title: "Confidence from offer to keys.",
          desc: "We keep you informed at every step, safeguard your earnest money, and make certain the home you're buying is truly yours — with clear title and no surprises at the table.",
        },
        {
          icon: Home,
          label: "For Sellers",
          title: "A smooth, on-time close.",
          desc: "We resolve title matters early, protect your proceeds, and move your sale to the closing table without last-minute stress — so you can focus on what's next.",
        },
        {
          icon: Handshake,
          label: "For Agents",
          title: "A partner who makes you shine.",
          desc: "Proactive updates, dependable timelines and a white-glove experience your clients remember — and refer. We help you close with confidence, every single time.",
        },
      ],
    },
    offerings: [
      { icon: Home, title: "Purchase & Sale Closings", desc: "Seamless coordination for buyers and sellers, from contract to keys." },
      { icon: FileSearch, title: "Title Search & Insurance", desc: "Thorough title examination and owner's and lender's policies that protect your investment." },
      { icon: Repeat, title: "Refinance & Home Equity", desc: "Efficient handling of refinances and home-equity transactions." },
      { icon: KeyRound, title: "Escrow & Earnest Money", desc: "Secure, transparent management of escrow and earnest funds." },
    ],
    points: [
      "Proactive communication at every milestone",
      "A dedicated closer for every file",
      "Clear, jargon-free explanations",
      "On-time closings, every time",
    ],
  },

  commercial: {
    slug: "commercial",
    eyebrow: "Commercial",
    title: "Sophisticated title & escrow for commercial real estate.",
    subtitle:
      "Complex, high-value commercial transactions demand precision. We deliver it — quietly, rigorously and reliably.",
    heroImg: IMAGES.skyscraperDusk,
    introImg: IMAGES.commercialLobby,
    introHeading: "Built for high-stakes transactions",
    introBody:
      "Office, retail, industrial and mixed-use closings carry layered ownership, financing and diligence requirements. Our team brings the rigor and responsiveness that commercial clients and their counsel expect from start to finish.",
    offerings: [
      { icon: Building2, title: "Commercial Closings", desc: "End-to-end coordination for acquisitions, dispositions and leases." },
      { icon: ShieldCheck, title: "Title Examination & Underwriting", desc: "Deep due diligence and clear title for commercial assets." },
      { icon: Scale, title: "Sophisticated Escrow", desc: "Structured escrow for multi-party and complex transactions." },
      { icon: Repeat, title: "1031 Exchange Support", desc: "Coordination for like-kind exchange timelines and requirements." },
    ],
    points: [
      "Experienced with multi-party structures",
      "Responsive to counsel and lenders",
      "Rigorous title diligence",
      "Confidential and efficient",
    ],
  },

  builders: {
    slug: "builders",
    eyebrow: "Builders",
    title: "A title partner engineered around your build schedule.",
    subtitle:
      "Reliable coordination and lot-by-lot closings that keep your projects moving — phase after phase.",
    heroImg: IMAGES.builders,
    introImg: IMAGES.conference,
    introHeading: "Keeping pace with your pipeline",
    introBody:
      "Builders can't afford closing delays. We align our process with your construction timeline — from lot acquisition through individual home sales — so every phase closes on schedule and nothing slows your momentum.",
    offerings: [
      { icon: Map, title: "Lot Acquisition Closings", desc: "Smooth acquisition of lots and land for your next community." },
      { icon: Home, title: "New-Construction Sales", desc: "Buyer closings coordinated to your delivery timeline." },
      { icon: Coins, title: "Builder Escrow Accounts", desc: "Dedicated, transparent escrow tailored to builders." },
      { icon: Layers, title: "Volume Coordination", desc: "Streamlined processing across many lots at once." },
    ],
    points: [
      "A dedicated builder-services team",
      "Predictable, on-schedule closings",
      "Streamlined multi-lot processing",
      "A single point of contact",
    ],
  },

  developers: {
    slug: "developers",
    eyebrow: "Developers",
    title: "Structured title & escrow for complex developments.",
    subtitle:
      "From raw land to finished community, we support developments through every phase with foresight and structure.",
    heroImg: IMAGES.heroSkyline,
    introImg: IMAGES.skyscraperDusk,
    introHeading: "Clarity through complexity",
    introBody:
      "Multi-phase developments involve intricate ownership, financing and platting. Houston Title Co. anticipates each stage — bringing curative title work and coordination that keep your project on solid ground from acquisition to build-out.",
    offerings: [
      { icon: Map, title: "Land Acquisition", desc: "Diligent closings for raw land and assemblage." },
      { icon: FileStack, title: "Subdivision & Platting Support", desc: "Title coordination through platting and entitlement." },
      { icon: Building2, title: "Development Escrow", desc: "Construction and development escrow structured to your capital stack." },
      { icon: Layers, title: "Phased Take-Down Closings", desc: "Orchestrated closings across each phase of your project." },
    ],
    points: [
      "Experience with multi-phase projects",
      "Proactive title curative work",
      "Coordination with lenders & counsel",
      "A long-horizon partnership",
    ],
  },

  investors: {
    slug: "investors",
    eyebrow: "Investors",
    title: "Discreet, efficient closings for portfolios of every scale.",
    subtitle:
      "Whether one property or one hundred, we make investment transactions seamless, fast and confidential.",
    heroImg: IMAGES.penthouse,
    introImg: IMAGES.marbleInterior,
    introHeading: "Built for volume and velocity",
    introBody:
      "Investors move quickly. Our team delivers fast, accurate closings and the reporting you need — for single acquisitions, bulk portfolios and everything in between — without ever sacrificing diligence.",
    offerings: [
      { icon: TrendingUp, title: "Single & Bulk Acquisitions", desc: "One property or an entire portfolio, closed with precision." },
      { icon: Repeat, title: "1031 Exchange Coordination", desc: "Support that keeps your exchange timelines on track." },
      { icon: Building, title: "Portfolio Title Services", desc: "Ongoing title work for growing real-estate portfolios." },
      { icon: Users, title: "Entity & LLC Closings", desc: "Closings structured around your investment entities." },
    ],
    points: [
      "Fast, dependable turnaround",
      "Volume-ready processes",
      "Discretion and confidentiality",
      "Clear, consistent reporting",
    ],
  },
};
